import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { openPdf } from "@/lib/pdf";
import { Search, Printer, RefreshCcw, X, Calendar as CalendarIcon, Receipt, ExternalLink, Tag, FileText, XCircle } from "lucide-react";
import { toast } from "sonner";

const PAYMENTS = [
  { v: "", l: "Alle Zahlarten" },
  { v: "bar", l: "Bar" },
  { v: "karte", l: "Karte" },
  { v: "gutschein", l: "Gutschein" },
  { v: "einladung", l: "Einladung" },
  { v: "frei", l: "Kostenlos" },
  { v: "mitglied", l: "Mitgliedskarte" },
];
const payLabel = (v) => PAYMENTS.find((p) => p.v === v)?.l || v;

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

export default function Orders() {
  const [rows, setRows] = useState([]);
  const [totalSum, setTotalSum] = useState(0);
  const [busy, setBusy] = useState(false);
  const [dateFrom, setDateFrom] = useState(daysAgo(30));
  const [dateTo, setDateTo] = useState(today());
  const [paymentMethod, setPaymentMethod] = useState("");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [stornoTarget, setStornoTarget] = useState(null); // {order} → opens StornoModal

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (paymentMethod) params.set("payment_method", paymentMethod);
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/orders?${params.toString()}`);
      setRows(data.orders || []);
      setTotalSum(data.total_sum || 0);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  // Initial load beim Mount
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const byPay = {};
    const active = rows.filter((o) => o.status !== "storniert");
    for (const o of active) {
      const k = o.payment_method || "?";
      byPay[k] = (byPay[k] || 0) + (o.total || 0);
    }
    const canceled = rows.length - active.length;
    return { total: active.length, canceled, sum: totalSum, avg: active.length ? totalSum / active.length : 0, byPay };
  }, [rows, totalSum]);

  const reprint = async (id) => {
    try {
      const { data } = await api.post(`/pos/receipt-print/${id}`);
      toast.success(`Gedruckt (${data.printer})`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const cancelOrder = (order) => {
    setStornoTarget(order);
  };
  const confirmStorno = async ({ refund_method, refund_note, voucher_email }) => {
    if (!stornoTarget) return;
    try {
      const { data } = await api.post(`/orders/${stornoTarget.id}/cancel`, {
        refund_method, refund_note, voucher_email,
      });
      const msg = data.refund_voucher
        ? `Storniert · Gutschein ${data.refund_voucher.code} über ${data.refund_amount.toFixed(2)} € erstellt`
        : `Storniert · ${data.refund_amount.toFixed(2)} € rückerstattet (${refund_method})`;
      toast.success(msg);
      setStornoTarget(null);
      setDetail(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Storno fehlgeschlagen");
    }
  };
  const openDrawer = async () => {
    try {
      const { data } = await api.post("/pos/drawer-open", { printer_type: "receipt" });
      toast.success(`Schublade offen (${data.printer})`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Kassenbuch</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl">Bestellhistorie</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={openDrawer} data-testid="orders-drawer-btn"
            className="border border-[#D4AF37]/40 text-[#D4AF37] px-3 sm:px-4 py-2 rounded text-[11px] uppercase tracking-widest hover:bg-[#D4AF37]/10 flex items-center gap-2">
            <Receipt size={14} /> <span className="hidden sm:inline">Kassenlade öffnen</span><span className="sm:hidden">Lade</span>
          </button>
          <button onClick={load} disabled={busy} data-testid="orders-refresh-btn"
            className="bg-[#D4AF37] text-black px-3 sm:px-4 py-2 rounded text-[11px] uppercase tracking-widest hover:bg-[#EEDC82] flex items-center gap-2">
            <RefreshCcw size={14} /> <span className="hidden sm:inline">Aktualisieren</span><span className="sm:hidden">Reload</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Anzahl" value={stats.total} sub={stats.canceled ? `${stats.canceled} storniert` : "Bestellungen"} />
        <Stat label="Umsatz" value={`${stats.sum.toFixed(2)} €`} sub="ohne Stornos" gold />
        <Stat label="Durchschnitt" value={`${stats.avg.toFixed(2)} €`} sub="Pro Bon" />
        <Stat label="Bar-Umsatz" value={`${(stats.byPay.bar || 0).toFixed(2)} €`} sub="Nur Bargeld" />
      </div>

      <div className="card-lux p-4 grid md:grid-cols-5 gap-3 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398] flex items-center gap-1"><CalendarIcon size={11} /> Von</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="orders-date-from"
            className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Bis</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="orders-date-to"
            className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Zahlart</label>
          <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} data-testid="orders-payment-filter"
            className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]">
            {PAYMENTS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
          </select>
        </div>
        <div className="md:col-span-1">
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Suche</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#575148]" size={13} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Bon-Nr, Gast, Film …" data-testid="orders-search"
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              className="w-full mt-1 pl-8 pr-3 py-2 bg-[#050505] border border-[#2A2A2A] rounded outline-none focus:border-[#D4AF37]" />
          </div>
        </div>
        <button onClick={load} disabled={busy} data-testid="orders-apply-btn"
          className="bg-[#D4AF37] text-black px-4 py-2 rounded text-[11px] uppercase tracking-widest hover:bg-[#EEDC82]">
          Anwenden
        </button>
      </div>

      <div className="card-lux overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-[#0A0A0A] border-b border-[#1A1A1A] text-[#A8A398] text-[10px] uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">Bon-Nr</th>
              <th className="text-left p-3">Datum &amp; Zeit</th>
              <th className="text-left p-3">Gast · Film</th>
              <th className="text-center p-3">Zahlart</th>
              <th className="text-right p-3">Rabatt</th>
              <th className="text-right p-3">Gutschein</th>
              <th className="text-right p-3">Gesamt</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => {
              const dt = new Date(o.created_at);
              const isCanceled = o.status === "storniert";
              return (
                <tr key={o.id} className={`border-b border-[#141414] hover:bg-[#0F0D08] cursor-pointer ${isCanceled ? "opacity-60" : ""}`} onClick={() => setDetail(o)} data-testid={`order-row-${o.id}`}>
                  <td className="p-3 font-mono text-[#D4AF37]">
                    {o.order_number}
                    {isCanceled && <div className="text-[9px] uppercase tracking-widest text-[#EE9CA1] mt-0.5">Storniert</div>}
                  </td>
                  <td className="p-3 text-[#F5F0E6] text-xs">
                    {dt.toLocaleDateString("de-DE")}<br />
                    <span className="text-[#A8A398] font-mono">{dt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}</span>
                  </td>
                  <td className="p-3">
                    <div className={isCanceled ? "line-through text-[#575148]" : ""}>{o.guest_name || "—"}</div>
                    {o.movie_title && <div className={`text-xs text-[#A8A398] ${isCanceled ? "line-through" : ""}`}>{o.movie_title}</div>}
                  </td>
                  <td className="p-3 text-center">
                    <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded border border-[#2A2A2A]">{payLabel(o.payment_method)}</span>
                  </td>
                  <td className="p-3 text-right font-mono text-[#A8A398]">{(o.discount || 0) > 0 ? `− ${o.discount.toFixed(2)}` : "—"}</td>
                  <td className="p-3 text-right font-mono text-[#A8A398]">
                    {o.voucher?.deducted ? `− ${o.voucher.deducted.toFixed(2)}` : "—"}
                  </td>
                  <td className={`p-3 text-right font-mono font-bold ${isCanceled ? "text-[#575148] line-through" : "text-[#D4AF37]"}`}>{(o.total || 0).toFixed(2)} €</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button onClick={(e) => { e.stopPropagation(); reprint(o.id); }} data-testid={`order-print-${o.id}`}
                      className="text-xs px-2 py-1 border border-[#D4AF37]/40 rounded hover:bg-[#D4AF37] hover:text-black text-[#D4AF37] flex items-center gap-1 mx-auto">
                      <Printer size={12} /> Drucken
                    </button>
                    {o.ticket_ids?.length > 0 && (
                      <button onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            let n = 0;
                            for (const tid of o.ticket_ids) {
                              try { await api.post(`/tickets/${tid}/print-label`); n++; } catch { /* skip */ }
                            }
                            if (n > 0) toast.success(`${n} Ticket-Etikett${n > 1 ? "s" : ""} gedruckt`);
                            else toast.error("Etikettendrucker nicht erreichbar");
                          } catch (err) { toast.error("Fehler beim Etikettendruck"); }
                        }} data-testid={`order-label-${o.id}`}
                        className="text-xs px-2 py-1 border border-[#D4AF37]/40 rounded hover:bg-[#D4AF37] hover:text-black text-[#D4AF37] flex items-center gap-1 mx-auto mt-1">
                        <Tag size={12} /> Etikett
                      </button>
                    )}
                    {o.ticket_ids?.length > 0 && (
                      <>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/tickets/${o.ticket_ids[0]}/pdf`} target="_blank" rel="noreferrer"
                          onClick={(e) => {
                            e.preventDefault(); e.stopPropagation();
                            o.ticket_ids.forEach((tid, i) => setTimeout(() => openPdf(`/tickets/${tid}/pdf`), i * 250));
                          }}
                          data-testid={`order-tickets-pdf-${o.id}`}
                          title={`Alle ${o.ticket_ids.length} A4-Tickets als PDF öffnen`}
                          className="text-xs px-2 py-1 border border-[#EEDC82]/40 rounded hover:bg-[#EEDC82] hover:text-black text-[#EEDC82] flex items-center gap-1 mx-auto mt-1">
                          <FileText size={12} /> A4-PDF
                        </a>
                        <button onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              let n = 0;
                              for (const tid of o.ticket_ids) {
                                try { await api.post(`/tickets/${tid}/print-office`); n++; } catch { /* skip */ }
                              }
                              if (n > 0) toast.success(`${n} A4-Ticket${n > 1 ? "s" : ""} an Bürodrucker`);
                              else {
                                toast.info("Bürodrucker nicht erreichbar — PDF wird geöffnet");
                                o.ticket_ids.forEach((tid, i) => setTimeout(() => openPdf(`/tickets/${tid}/pdf`), i * 250));
                              }
                            } catch (err) {
                              toast.info("PDF wird geöffnet");
                              o.ticket_ids.forEach((tid, i) => setTimeout(() => openPdf(`/tickets/${tid}/pdf`), i * 250));
                            }
                          }} data-testid={`order-office-${o.id}`}
                          className="text-xs px-2 py-1 border border-[#8FBC8F]/40 rounded hover:bg-[#8FBC8F] hover:text-black text-[#8FBC8F] flex items-center gap-1 mx-auto mt-1">
                          <Printer size={12} /> A4-Druck
                        </button>
                      </>
                    )}
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${o.id}/invoice.pdf`} target="_blank" rel="noreferrer"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); openPdf(`/orders/${o.id}/invoice.pdf`); }} data-testid={`order-invoice-pdf-${o.id}`}
                      className="text-xs px-2 py-1 border border-[#D4AF37]/40 rounded hover:bg-[#D4AF37] hover:text-black text-[#D4AF37] flex items-center gap-1 mx-auto">
                      <FileText size={12} /> Rechnung
                    </a>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${o.id}/receipt.pdf`} target="_blank" rel="noreferrer"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); openPdf(`/orders/${o.id}/receipt.pdf`); }} data-testid={`order-receipt-pdf-${o.id}`}
                      className="text-xs px-2 py-1 border border-[#A8A398]/40 rounded hover:bg-[#A8A398] hover:text-black text-[#A8A398] flex items-center gap-1 mx-auto mt-1">
                      <Receipt size={12} /> Bon PDF
                    </a>
                    <button onClick={async (e) => {
                        e.stopPropagation();
                        try { await api.post(`/orders/${o.id}/print-office`); toast.success("A4-Beleg an Bürodrucker"); }
                        catch (err) {
                          toast.info("Bürodrucker nicht erreichbar — PDF wird geöffnet");
                          openPdf(`/orders/${o.id}/receipt.pdf`);
                        }
                      }} data-testid={`order-office-receipt-${o.id}`}
                      className="text-xs px-2 py-1 border border-[#8FBC8F]/40 rounded hover:bg-[#8FBC8F] hover:text-black text-[#8FBC8F] flex items-center gap-1 mx-auto mt-1">
                      <Printer size={12} /> Drucken
                    </button>
                    {!isCanceled && (
                      <button onClick={(e) => { e.stopPropagation(); cancelOrder(o); }} data-testid={`order-cancel-${o.id}`}
                        className="text-xs px-2 py-1 border border-[#7A1921] rounded hover:bg-[#7A1921] hover:text-white text-[#EE9CA1] flex items-center gap-1 mx-auto mt-1">
                        <XCircle size={12} /> Storno
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!rows.length && !busy && (
              <tr><td colSpan={8} className="p-10 text-center text-[#A8A398]">Keine Bestellungen im Zeitraum</td></tr>
            )}
            {busy && <tr><td colSpan={8} className="p-10 text-center text-[#A8A398]">Lade …</td></tr>}
          </tbody>
        </table>
        </div>
      </div>

      {detail && <OrderDetail order={detail} onClose={() => setDetail(null)} onPrint={() => reprint(detail.id)} onCancel={() => cancelOrder(detail)} />}
      {stornoTarget && <StornoModal order={stornoTarget} onClose={() => setStornoTarget(null)} onConfirm={confirmStorno} />}
    </div>
  );
}

function TicketPdfIcon() {
  return <FileText size={13} className="text-[#D4AF37]" />;
}

function OrderDetail({ order, onClose, onPrint, onCancel }) {
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  const dt = new Date(order.created_at);
  const items = order.items || [];
  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="order-detail-modal">
      <div className="relative w-full max-w-lg bg-[#0F0D08] border border-[#D4AF37]/40 rounded-lg p-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-[#F5F0E6]/70 hover:text-[#D4AF37]"><X size={22} /></button>
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] flex items-center gap-2"><Receipt size={13} /> Bestellung</div>
        <div className="font-heading text-3xl mt-1 text-[#D4AF37]">{order.order_number}</div>
        <div className="text-xs text-[#A8A398] mt-1">{dt.toLocaleDateString("de-DE")} · {dt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}</div>
        {order.status === "storniert" && (
          <div className="mt-3 px-3 py-2 rounded border border-[#7A1921] bg-[#7A1921]/20 text-[10px] uppercase tracking-[0.35em] text-[#EE9CA1] flex items-center gap-2">
            <XCircle size={13} /> Storniert am {new Date(order.cancelled_at || "").toLocaleString("de-DE")}
            {order.cancel_tse?.counter && <span className="ml-auto font-mono text-[#EE9CA1]/70">TSE #{order.cancel_tse.counter}</span>}
          </div>
        )}

        <div className="mt-4 space-y-1 text-sm">
          {order.guest_name && <Row label="Gast" value={order.guest_name} />}
          {order.movie_title && <Row label="Film" value={order.movie_title} />}
          <Row label="Zahlart" value={payLabel(order.payment_method)} />
        </div>

        {items.length > 0 && (
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-widest text-[#A8A398] mb-2">Positionen</div>
            <div className="space-y-1">
              {items.map((it, i) => (
                <div key={i} className="flex justify-between text-sm border-b border-[#1A1A1A] py-1">
                  <span>{it.quantity}× {it.name}</span>
                  <span className="font-mono">{(it.line_total || 0).toFixed(2)} €</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {order.ticket_ids?.length > 0 && (
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-widest text-[#A8A398] mb-2 flex items-center justify-between">
              <span>Tickets ({order.ticket_ids.length})</span>
              <button onClick={() => order.ticket_ids.forEach((tid) => openPdf(`/tickets/${tid}/pdf`))}
                data-testid="order-detail-open-all-tickets"
                className="text-[9px] uppercase tracking-widest px-2 py-0.5 border border-[#D4AF37]/40 rounded text-[#D4AF37] hover:bg-[#D4AF37]/10">
                Alle als PDF öffnen
              </button>
            </div>
            <div className="space-y-1">
              {order.ticket_ids.map((tid, i) => (
                <a key={tid} href={`${process.env.REACT_APP_BACKEND_URL}/api/tickets/${tid}/pdf`} target="_blank" rel="noreferrer"
                  onClick={(e) => { e.preventDefault(); openPdf(`/tickets/${tid}/pdf`); }}
                  data-testid={`order-detail-ticket-pdf-${tid}`}
                  className="flex justify-between text-sm border-b border-[#1A1A1A] py-2 px-1 hover:bg-[#D4AF37]/5 rounded transitions-safe group">
                  <span className="flex items-center gap-2">
                    <TicketPdfIcon />
                    <span className="text-[#F5F0E6]">Ticket {i + 1}</span>
                    <span className="text-[10px] uppercase tracking-widest text-[#575148] font-mono">{tid.slice(0, 8)}…</span>
                  </span>
                  <span className="text-[10px] uppercase tracking-widest text-[#D4AF37] opacity-70 group-hover:opacity-100">PDF ↗</span>
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="mt-5 space-y-1 text-sm">
          {(order.discount || 0) > 0 && <Row label="Rabatt" value={`− ${order.discount.toFixed(2)} €`} mono />}
          {order.voucher?.deducted > 0 && <Row label={`Gutschein ${order.voucher.code || ""}`} value={`− ${order.voucher.deducted.toFixed(2)} €`} mono />}
          <div className="flex justify-between text-base border-t border-[#1A1A1A] pt-2 mt-2">
            <span className="font-heading">GESAMT</span>
            <span className="font-heading text-[#D4AF37]">{(order.total || 0).toFixed(2)} €</span>
          </div>
        </div>

        <div className="mt-6 flex gap-2 flex-wrap">
          <button onClick={onPrint} data-testid="order-detail-print"
            className="flex-1 min-w-[180px] bg-[#D4AF37] text-black px-4 py-3 rounded text-[11px] uppercase tracking-[0.4em] hover:bg-[#EEDC82] flex items-center justify-center gap-2">
            <Printer size={14} /> An Bondrucker
          </button>
          <button onClick={async () => {
              try { await api.post(`/orders/${order.id}/print-office`); toast.success("A4-Beleg an Bürodrucker"); }
              catch (err) {
                toast.info("Bürodrucker nicht erreichbar — PDF wird geöffnet");
                openPdf(`/orders/${order.id}/receipt.pdf`);
              }
            }} data-testid="order-detail-print-office"
            className="flex-1 min-w-[160px] border border-[#8FBC8F]/40 text-[#8FBC8F] px-4 py-3 rounded text-[11px] uppercase tracking-[0.4em] hover:bg-[#8FBC8F]/10 flex items-center justify-center gap-2">
            <FileText size={14} /> A4-Beleg drucken
          </button>
          <button onClick={async () => {
              try { await api.post(`/orders/${order.id}/print-office-invoice`); toast.success("Rechnung an Bürodrucker"); }
              catch (err) {
                toast.info("Bürodrucker nicht erreichbar — PDF wird geöffnet");
                openPdf(`/orders/${order.id}/invoice.pdf`);
              }
            }} data-testid="order-detail-print-invoice"
            className="flex-1 min-w-[160px] border border-[#8FBC8F]/40 text-[#8FBC8F] px-4 py-3 rounded text-[11px] uppercase tracking-[0.4em] hover:bg-[#8FBC8F]/10 flex items-center justify-center gap-2">
            <FileText size={14} /> Rechnung drucken
          </button>
          {order.ticket_ids?.[0] && (
            <>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/tickets/${order.ticket_ids[0]}/pdf`} target="_blank" rel="noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  order.ticket_ids.forEach((tid, i) => setTimeout(() => openPdf(`/tickets/${tid}/pdf`), i * 250));
                }}
                data-testid="order-detail-a4-tickets-preview"
                title={`Alle ${order.ticket_ids.length} A4-Tickets als PDF öffnen`}
                className="flex-1 min-w-[160px] border border-[#EEDC82]/40 text-[#EEDC82] px-4 py-3 rounded text-[11px] uppercase tracking-[0.4em] hover:bg-[#EEDC82]/10 flex items-center justify-center gap-2">
                <FileText size={14} /> A4-Tickets · PDF
              </a>
              <button onClick={async () => {
                  try {
                    let n = 0;
                    for (const tid of order.ticket_ids) {
                      try { await api.post(`/tickets/${tid}/print-office`); n++; } catch { /* skip */ }
                    }
                    if (n > 0) toast.success(`${n} A4-Ticket${n > 1 ? "s" : ""} gedruckt`);
                    else {
                      toast.info("Bürodrucker nicht erreichbar — PDFs werden geöffnet");
                      order.ticket_ids.forEach((tid, i) => setTimeout(() => openPdf(`/tickets/${tid}/pdf`), i * 250));
                    }
                  } catch (err) {
                    toast.info("PDFs werden geöffnet");
                    order.ticket_ids.forEach((tid, i) => setTimeout(() => openPdf(`/tickets/${tid}/pdf`), i * 250));
                  }
                }} data-testid="order-detail-print-a4-tickets"
                className="flex-1 min-w-[160px] border border-[#8FBC8F]/40 text-[#8FBC8F] px-4 py-3 rounded text-[11px] uppercase tracking-[0.4em] hover:bg-[#8FBC8F]/10 flex items-center justify-center gap-2">
                <Printer size={14} /> A4-Tickets · Drucken
              </button>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/tickets/${order.ticket_ids[0]}/pdf`} target="_blank" rel="noreferrer"
                onClick={(e) => { e.preventDefault(); openPdf(`/tickets/${order.ticket_ids[0]}/pdf`); }}
                className="px-4 py-3 border border-[#D4AF37]/40 rounded text-[11px] uppercase tracking-[0.4em] text-[#D4AF37] hover:bg-[#D4AF37]/10 flex items-center gap-2">
                <ExternalLink size={12} /> PDF
              </a>
            </>
          )}
        </div>
        {order.status !== "storniert" && onCancel && (
          <div className="mt-4 pt-4 border-t border-[#1A1A1A]">
            <button onClick={onCancel} data-testid="order-detail-cancel"
              className="w-full border border-[#7A1921] text-[#EE9CA1] px-4 py-3 rounded text-[11px] uppercase tracking-[0.4em] hover:bg-[#7A1921] hover:text-white flex items-center justify-center gap-2">
              <XCircle size={14} /> Bestellung stornieren · Tickets & Bestand rückbuchen
            </button>
            <div className="mt-2 text-[10px] text-[#575148] text-center">
              Erzeugt TSE-Storno-Beleg (DSFinV-K). Kann nicht rückgängig gemacht werden.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between border-b border-[#1A1A1A] py-1">
      <span className="text-[#A8A398]">{label}</span>
      <span className={mono ? "font-mono" : ""}>{value}</span>
    </div>
  );
}
function Stat({ label, value, sub, gold }) {
  return (
    <div className="card-lux p-4">
      <div className="text-[9px] uppercase tracking-[0.35em] text-[#A8A398]">{label}</div>
      <div className={`font-heading text-3xl mt-1 ${gold ? "text-[#D4AF37]" : "text-[#F5F0E6]"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#575148] mt-1">{sub}</div>
    </div>
  );
}

const REFUND_METHODS = [
  { v: "bar", l: "Bar aus Kasse", desc: "Kunde erhält den Betrag in bar zurück" },
  { v: "karte", l: "Karte (EC)", desc: "Rückbuchung über EC-Terminal — muss manuell durchgeführt werden" },
  { v: "karte_stripe", l: "Karte · Stripe-Refund", desc: "Automatische Stripe-Rückerstattung" },
  { v: "ueberweisung", l: "Überweisung", desc: "Rücküberweisung — Kontodaten vom Kunden erfassen" },
  { v: "gutschein", l: "Gutschein erstellen", desc: "Neuer Gutschein über den Storno-Betrag wird ausgestellt" },
  { v: "keine", l: "Keine Erstattung", desc: "Beleg wird nur storniert (z. B. Testbuchung)" },
];

function StornoModal({ order, onClose, onConfirm }) {
  const [method, setMethod] = useState("bar");
  const [note, setNote] = useState("");
  const [voucherEmail, setVoucherEmail] = useState(order.guest_email || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  const submit = async () => {
    setBusy(true);
    try {
      await onConfirm({
        refund_method: method,
        refund_note: note,
        voucher_email: method === "gutschein" ? voucherEmail : undefined,
      });
    } finally { setBusy(false); }
  };
  const active = REFUND_METHODS.find((m) => m.v === method);
  return (
    <div className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="storno-modal">
      <div className="relative w-full max-w-lg bg-[#0F0D08] border border-[#7A1921]/50 rounded-lg p-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-[#F5F0E6]/70 hover:text-[#EE9CA1]"><X size={22} /></button>
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#EE9CA1] flex items-center gap-2"><XCircle size={13} /> Storno · Rückerstattung</div>
        <div className="font-heading text-3xl mt-1">{order.order_number}</div>
        <div className="text-sm text-[#A8A398] mt-1">
          Betrag: <span className="font-mono text-[#D4AF37] text-base">{(order.total || 0).toFixed(2)} €</span> · Zahlart ursprünglich: <span className="uppercase tracking-widest text-xs">{order.payment_method}</span>
        </div>

        <div className="mt-5">
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398] mb-2 block">Erstattungsart wählen</label>
          <div className="space-y-1.5" data-testid="storno-refund-methods">
            {REFUND_METHODS.map((m) => (
              <button key={m.v} onClick={() => setMethod(m.v)} data-testid={`storno-method-${m.v}`}
                className={`w-full text-left px-3 py-2.5 rounded border transitions-safe ${method === m.v ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#2A2A2A] hover:border-[#D4AF37]/40"}`}>
                <div className={`text-sm ${method === m.v ? "text-[#D4AF37]" : "text-[#F5F0E6]"}`}>{m.l}</div>
                <div className="text-[10px] text-[#575148] mt-0.5">{m.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {method === "gutschein" && (
          <div className="mt-4">
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Gutschein-Empfänger E-Mail (optional)</label>
            <input type="email" value={voucherEmail} onChange={(e) => setVoucherEmail(e.target.value)}
              placeholder="gast@example.com" data-testid="storno-voucher-email"
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
          </div>
        )}

        <div className="mt-4">
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Grund / Notiz (optional)</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
            placeholder="z. B. Verwechslung, Zahlungsfehler …" data-testid="storno-note"
            className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none text-sm" />
        </div>

        <div className="mt-5 p-3 rounded border border-[#7A1921]/30 bg-[#7A1921]/10 text-[10px] text-[#EE9CA1]">
          <b>Auswirkungen:</b> Tickets werden ungültig gestellt · Snack-Bestand rückgebucht · Gutschein-Guthaben gutgeschrieben ·
          TSE-Storno-Signatur (DSFinV-K) wird angelegt · Erstattung: <b className="text-[#D4AF37]">{active?.l}</b>
        </div>

        <div className="mt-6 flex gap-2">
          <button onClick={onClose} data-testid="storno-cancel"
            className="flex-1 border border-[#2A2A2A] text-[#A8A398] px-4 py-3 rounded text-[11px] uppercase tracking-[0.4em] hover:border-[#D4AF37] hover:text-[#F5F0E6]">
            Abbrechen
          </button>
          <button onClick={submit} disabled={busy} data-testid="storno-confirm"
            className="flex-1 bg-[#7A1921] text-white px-4 py-3 rounded text-[11px] uppercase tracking-[0.4em] hover:bg-[#951F27] disabled:opacity-40 flex items-center justify-center gap-2">
            <XCircle size={14} /> {busy ? "Storniere …" : "Storno bestätigen"}
          </button>
        </div>
      </div>
    </div>
  );
}
